import React from 'react';
import SectionCard from '../../components/properties/controls/SectionCard';
import Slider from '../../components/properties/controls/Slider';
import Button from '../../components/properties/controls/Button';
import Toggle from '../../components/properties/controls/Toggle';
import Segmented from '../../components/properties/controls/Segmented';
import styles from './ImageToolPanel.module.css';

const ImageEraserPanel = ({ model, disabled, onClear, onDone }) => {
  const mode = model.mixed('eraser.mode');
  const brushSize = model.mixed('eraser.brushSize');
  const hardness = model.mixed('eraser.hardness');
  const opacity = model.mixed('eraser.opacity');
  const tolerance = model.mixed('eraser.magic.tolerance');
  const contiguous = model.mixed('eraser.magic.contiguous');
  const feather = model.mixed('eraser.magic.feather');
  const expand = model.mixed('eraser.magic.expand');
  const opMode = model.mixed('eraser.magic.opMode');

  const selectedElements = model.selectedElements || [];
  const magicOpsCount = selectedElements.reduce(
    (acc, el) => Math.max(acc, el.eraser?.magic?.ops?.length || 0),
    0,
  );
  const pixelOpsCount = selectedElements.reduce(
    (acc, el) => Math.max(acc, el.eraser?.strokes?.length || 0),
    0,
  );

  const currentMode = mode.kind === 'single' ? mode.value : 'pixel';
  const isMagic = currentMode === 'magic';

  const handleMode = (nextMode) => {
    model.applyToSelection(
      (el) => ({
        eraser: {
          enabled: true,
          mode: nextMode,
          magic: {
            ...(el.eraser?.magic || {}),
            enabled: nextMode === 'magic' ? true : el.eraser?.magic?.enabled,
          },
        },
      }),
      'immediate',
      'eraser-mode',
    );
  };

  const handleClear = () => {
    if (isMagic) {
      model.applyToSelection(
        (el) => {
          const hasPixel = (el.eraser?.strokes || []).length > 0;
          return {
            eraser: {
              enabled: hasPixel,
              magic: {
                ...(el.eraser?.magic || {}),
                ops: [],
              },
            },
          };
        },
        'immediate',
        'magic-clear',
      );
    } else {
      model.applyToSelection(
        (el) => {
          const hasMagic = (el.eraser?.magic?.ops || []).length > 0;
          return {
            eraser: {
              enabled: hasMagic,
              strokes: [],
            },
          };
        },
        'immediate',
        'eraser-clear',
      );
    }
    if (onClear) onClear();
  };

  const handleUndoMagic = () => {
    model.applyToSelection(
      (el) => {
        const ops = el.eraser?.magic?.ops || [];
        if (!ops.length) return null;
        const nextOps = ops.slice(0, -1);
        const hasPixel = (el.eraser?.strokes || []).length > 0;
        return {
          eraser: {
            enabled: hasPixel || nextOps.length > 0,
            magic: {
              ...(el.eraser?.magic || {}),
              ops: nextOps,
            },
          },
        };
      },
      'immediate',
      'magic-undo',
    );
  };

  return (
    <div className={styles.toolContent}>
      <div className={styles.eraserModes}>
        <button
          type="button"
          className={`${styles.modeButton} ${
            currentMode === 'pixel' ? styles.modeButtonActive : ''
          }`}
          onClick={() => handleMode('pixel')}
          disabled={disabled}
        >
          Pixel
        </button>
        <button
          type="button"
          className={`${styles.modeButton} ${
            currentMode === 'magic' ? styles.modeButtonActive : ''
          }`}
          onClick={() => handleMode('magic')}
          disabled={disabled}
        >
          Magic
        </button>
      </div>

      {!isMagic && (
        <SectionCard title="Brush">
          <Slider
            label="Size"
            min={4}
            max={200}
            step={1}
            value={brushSize.kind === 'mixed' ? 24 : brushSize.value}
            mixed={brushSize.kind === 'mixed'}
            onChange={(value) =>
              model.applyToSelection(
                { eraser: { brushSize: value } },
                'commitOnRelease',
                'eraser-size',
              )
            }
            onCommit={() => model.commitPending('eraser-size')}
            disabled={disabled}
          />
          <Slider
            label="Hardness"
            min={0}
            max={1}
            step={0.01}
            value={hardness.kind === 'mixed' ? 0.8 : hardness.value}
            mixed={hardness.kind === 'mixed'}
            onChange={(value) =>
              model.applyToSelection(
                { eraser: { hardness: value } },
                'commitOnRelease',
                'eraser-hardness',
              )
            }
            onCommit={() => model.commitPending('eraser-hardness')}
            disabled={disabled}
          />
          <Slider
            label="Opacity"
            min={0}
            max={1}
            step={0.01}
            value={opacity.kind === 'mixed' ? 1 : opacity.value}
            mixed={opacity.kind === 'mixed'}
            onChange={(value) =>
              model.applyToSelection(
                { eraser: { opacity: value } },
                'commitOnRelease',
                'eraser-opacity',
              )
            }
            onCommit={() => model.commitPending('eraser-opacity')}
            disabled={disabled}
          />
        </SectionCard>
      )}

      {isMagic && (
        <>
          <SectionCard title="Magic">
            <Slider
              label="Tolerance"
              min={0}
              max={100}
              step={1}
              value={tolerance.kind === 'mixed' ? 25 : tolerance.value}
              mixed={tolerance.kind === 'mixed'}
              onChange={(value) =>
                model.applyToSelection(
                  { eraser: { magic: { tolerance: value } } },
                  'commitOnRelease',
                  'magic-tolerance',
                )
              }
              onCommit={() => model.commitPending('magic-tolerance')}
              disabled={disabled}
            />
            <Toggle
              checked={contiguous.kind === 'mixed' ? true : Boolean(contiguous.value)}
              indeterminate={contiguous.kind === 'mixed'}
              onChange={(value) =>
                model.applyToSelection(
                  { eraser: { magic: { contiguous: value } } },
                  'immediate',
                  'magic-contiguous',
                )
              }
              label="Contiguous"
              disabled={disabled}
            />
            <Slider
              label="Feather"
              min={0}
              max={20}
              step={1}
              value={feather.kind === 'mixed' ? 2 : feather.value}
              mixed={feather.kind === 'mixed'}
              onChange={(value) =>
                model.applyToSelection(
                  { eraser: { magic: { feather: value } } },
                  'commitOnRelease',
                  'magic-feather',
                )
              }
              onCommit={() => model.commitPending('magic-feather')}
              disabled={disabled}
            />
            <Slider
              label="Expand"
              min={-10}
              max={10}
              step={1}
              value={expand.kind === 'mixed' ? 0 : expand.value}
              mixed={expand.kind === 'mixed'}
              onChange={(value) =>
                model.applyToSelection(
                  { eraser: { magic: { expand: value } } },
                  'commitOnRelease',
                  'magic-expand',
                )
              }
              onCommit={() => model.commitPending('magic-expand')}
              disabled={disabled}
            />
            <div className={styles.toolNote}>Mode</div>
            <Segmented
              options={[
                { label: 'Add', value: 'add' },
                { label: 'Subtract', value: 'subtract' },
              ]}
              value={opMode.kind === 'mixed' ? 'add' : opMode.value}
              onChange={(value) =>
                model.applyToSelection(
                  { eraser: { magic: { opMode: value } } },
                  'immediate',
                  'magic-mode',
                )
              }
              disabled={disabled}
            />
          </SectionCard>
          <div className={styles.toolNote}>
            Click on the image to remove an area. Use Subtract to restore.
          </div>
        </>
      )}

      <div className={styles.eraserActions}>
        {isMagic ? (
          <Button onClick={handleUndoMagic} disabled={disabled || magicOpsCount === 0}>
            Undo last
          </Button>
        ) : null}
        <Button
          onClick={handleClear}
          disabled={disabled || (isMagic ? magicOpsCount === 0 : pixelOpsCount === 0)}
        >
          Clear all
        </Button>
        <Button onClick={onDone} variant="secondary">
          Done
        </Button>
      </div>
      {!isMagic && (
        <div className={styles.toolNote}>
          Paint directly on the image to erase. Use "Done" to exit.
        </div>
      )}
    </div>
  );
};

export default ImageEraserPanel;
